import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Grid, Message, Segment } from "semantic-ui-react";
import AuthService from "../../services/Auth";
import { doRequest } from "../../services/helpers";

const MAX_STATE_LENGTH = 512;

const isValidCallbackUri = uri => {
  try {
    const parsed = new URL(uri);
    return parsed.protocol === "https:";
  } catch {
    return false;
  }
};

const OAuthLogin = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [error, setError] = useState("");
  const [authorizing, setAuthorizing] = useState(true);

  const callbackUri = searchParams.get("callback_uri");
  const rawState = searchParams.get("state") || "";
  const state = rawState.slice(0, MAX_STATE_LENGTH);
  const isLoggedIn = AuthService.isLoggedIn();

  // Redirect to login if not authenticated
  useEffect(() => {
    if (callbackUri && isValidCallbackUri(callbackUri) && !isLoggedIn) {
      sessionStorage.setItem(
        "oauth_auth_pending",
        JSON.stringify({ callbackUri, state })
      );
      navigate("/login", { replace: true });
    }
  }, [callbackUri, state, isLoggedIn, navigate]);

  // Perform authorization (async)
  useEffect(() => {
    if (!callbackUri || !isLoggedIn) return;

    const authorize = async () => {
      try {
        const response = await doRequest("POST", "/auth/oauth_authorize", {
          data: { callback_uri: callbackUri, state },
        });
        const code = response.code;
        const serverState = response.state || "";
        const redirectUrl = new URL(callbackUri);
        redirectUrl.searchParams.set("code", code);
        if (serverState) {
          redirectUrl.searchParams.set("state", serverState);
        }
        window.location.href = redirectUrl.toString();
      } catch {
        setError("Authorization failed. Please try again.");
        setAuthorizing(false);
      }
    };

    authorize();
  }, [callbackUri, state, isLoggedIn]);

  if (!callbackUri) {
    return (
      <Grid centered verticalAlign="middle" style={{ height: "100vh" }}>
        <Grid.Column style={{ maxWidth: 450 }}>
          <Segment basic>
            <Message
              error
              header="Authorization Error"
              content="Missing callback_uri parameter."
            />
          </Segment>
        </Grid.Column>
      </Grid>
    );
  }

  if (!isValidCallbackUri(callbackUri)) {
    return (
      <Grid centered verticalAlign="middle" style={{ height: "100vh" }}>
        <Grid.Column style={{ maxWidth: 450 }}>
          <Segment basic>
            <Message
              error
              header="Authorization Error"
              content="Invalid callback URI: only HTTPS redirects are allowed."
            />
          </Segment>
        </Grid.Column>
      </Grid>
    );
  }

  if (error) {
    return (
      <Grid centered verticalAlign="middle" style={{ height: "100vh" }}>
        <Grid.Column style={{ maxWidth: 450 }}>
          <Segment basic>
            <Message error header="Authorization Error" content={error} />
          </Segment>
        </Grid.Column>
      </Grid>
    );
  }

  return (
    <Grid centered verticalAlign="middle" style={{ height: "100vh" }}>
      <Grid.Column style={{ maxWidth: 450 }}>
        <Segment basic loading={authorizing}>
          <p>Authorizing...</p>
        </Segment>
      </Grid.Column>
    </Grid>
  );
};

export default OAuthLogin;
